import { useCallback } from "react"
import type { Me } from "@/api/generated/model"
import { useMe } from "./session"
import { isStaff } from "./staff"

/** What a route asks of its caller (the access half of `RouteDef`, LLD §7.1). */
export interface RouteAccess {
  /** Every listed permission is required (spec x-permission semantics). */
  permission?: string | readonly string[] | undefined
  /** Any staff permission (the admin overview). */
  staff?: boolean | undefined
  /** Account routes admit staff without `permission` (backend DV-55). */
  staffExempt?: boolean | undefined
  /** Money routes do not exist in standalone. */
  money?: boolean | undefined
}

export const asArray = (p: string | readonly string[] | undefined): string[] =>
  p === undefined ? [] : typeof p === "string" ? [p] : [...p]

/** Whether this mode shows money at all (LLD §6.4). */
export const isMoneyVisible = (mode: Me["mode"] | undefined): boolean => mode === "normal"

/**
 * The one access rule (LLD §6.5): the route guard, the navigation, the palette
 * and the shortcuts all ask this, so a route a caller may not open is absent
 * everywhere at once rather than linked from one place and refused in another.
 */
export function routeAllowed(r: RouteAccess, me: Pick<Me, "permissions" | "mode">): boolean {
  if (r.money && !isMoneyVisible(me.mode)) return false
  const staff = isStaff(me.permissions)
  if (r.staff && !staff) return false
  if (r.staffExempt && staff) return true
  return asArray(r.permission).every((p) => me.permissions.includes(p))
}

function holder(me: Me | undefined) {
  const held = new Set(me?.permissions ?? [])
  return (p: string) => held.has(p)
}

export function useCan(): (p: string) => boolean {
  const { data: me } = useMe()
  return useCallback((p: string) => holder(me)(p), [me])
}

/** x-permission lists are "every one". */
export function useCanAll(): (ps: readonly string[]) => boolean {
  const { data: me } = useMe()
  return useCallback((ps: readonly string[]) => ps.every(holder(me)), [me])
}

export function useCanAny(): (ps: readonly string[]) => boolean {
  const { data: me } = useMe()
  return useCallback((ps: readonly string[]) => ps.some(holder(me)), [me])
}
